import { useMemo } from 'react';
import { decisionsMessageRef, questionRounds, suggestionRounds, type QuestionRound, type SuggestionRound } from '@shared/factory-rounds';
import type { TaskEvent, TaskMember } from '@shared/types/tasks';
import { formatDateTime } from '@/lib/formatters';
import { FactoryQuestionsCard } from './FactoryQuestionsCard';
import { FactorySuggestionsCard } from './FactorySuggestionsCard';
import { decisionRoundId } from './folding-card';
import { sendMessage } from './task-files';
import { formatRelative, memberName } from './task-ui';

type Card =
  | { kind: 'questions'; at: string; open: boolean; round: QuestionRound<TaskEvent> }
  | { kind: 'suggestions'; at: string; open: boolean; round: SuggestionRound<TaskEvent>; messageRef: string | null };

/**
 * T-55: the software factory's questions and suggestions on a task, pinned under the description, as
 * on the factory's own board. The ones still open come first, open to answer; answered and decided
 * rounds follow, newest first, folded to one line that opens on a click. The comments show a pointer
 * line in place of each round (TaskTimeline). Nothing shows on a task without any.
 */
export function TaskDecisions({ taskId, events, members }: { taskId: string; events: TaskEvent[]; members: TaskMember[] }) {
  const cards = useMemo(() => {
    const suggestions = suggestionRounds(events);
    const list: Card[] = [
      ...questionRounds(events).map((round): Card => ({ kind: 'questions', at: round.event.created_at, open: !round.answered, round })),
      ...suggestions.map(
        (round): Card => ({ kind: 'suggestions', at: round.event.created_at, open: !round.settled, round, messageRef: decisionsMessageRef(suggestions, round) })
      ),
    ];
    return list.sort((a, b) => Number(b.open) - Number(a.open) || b.at.localeCompare(a.at));
  }, [events]);
  if (!cards.length) return null;

  const meta = (event: TaskEvent, reply?: TaskEvent | null) => (
    <span data-hint={formatDateTime(new Date(event.created_at))}>
      {event.via_api_key_name ? `via ${event.via_api_key_name} · ` : null}
      {formatRelative(event.created_at)}
      {reply ? ` · answered by ${memberName(members.find((m) => m.user_id === reply.actor_user_id))}` : null}
    </span>
  );

  return (
    <section className="space-y-2" aria-label="Decisions">
      <h3 className="text-sm font-semibold">Decisions</h3>
      <div className="space-y-2">
        {cards.map((card) =>
          card.kind === 'questions' ? (
            <FactoryQuestionsCard
              key={card.round.event.id}
              id={decisionRoundId(card.round.event.id)}
              fold={!card.open}
              questions={card.round.questions}
              answered={card.round.answered}
              answers={card.round.answers}
              words={card.round.words}
              meta={meta(card.round.event, card.round.reply)}
              onAnswer={(message) => sendMessage(taskId, message)}
            />
          ) : (
            <FactorySuggestionsCard
              key={card.round.event.id}
              id={decisionRoundId(card.round.event.id)}
              fold={!card.open}
              suggestions={card.round.suggestions}
              decided={card.round.decided}
              meta={meta(card.round.event)}
              messageRef={card.messageRef}
              onApply={(message) => sendMessage(taskId, message)}
            />
          )
        )}
      </div>
    </section>
  );
}
